import { Box, Group, Tabs, Text, createStyles, useMantineTheme } from '@mantine/core'
import React, { useCallback, useMemo, useState } from 'react'
import { SkillCard } from '../SkillCard'
import { Images } from 'src/assets'
import { useMediaQuery } from '@mantine/hooks'
import Fade from 'react-reveal/Fade'

export interface SkillTabPanelProps {
  handleCategoryChange: (category: string) => void
}

enum SkillTabPanelList {
  Frontend = 'frontend',
  Backend = 'backend',
  Other = 'other',
}

const useStyles = createStyles(
  (theme, { backgroundColor, isMobile }: { backgroundColor: string; isMobile: boolean }) => {
    return {
      tabListWrapper: {
        backgroundColor: backgroundColor,
        padding: 4,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 30,
        marginBottom: 10,
        boxShadow: theme.shadows.sm,
      },
      skillsCardWrapper: {
        marginInline: isMobile ? 0 : -30,
        alignItems: 'center',
        justifyContent: 'center',
      },
    }
  },
)

export const SkillTabPanel: React.FC<SkillTabPanelProps> = props => {
  const { handleCategoryChange } = props
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.md})`)

  const { FrontEndSkills, BackEndSkills, OtherSkills } = Images
  const skillTabs: SkillTabPanelList[] = useMemo(
    () => [SkillTabPanelList.Frontend, SkillTabPanelList.Backend, SkillTabPanelList.Other],
    [],
  )
  const [tabIndex, setTabIndex] = useState<number>(0)
  const [activeTab, setActiveTab] = useState<string | null>(SkillTabPanelList.Frontend)

  const backgroundColor = useMemo(() => {
    return theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]
  }, [theme])

  const frontendSkills = useMemo(() => {
    return FrontEndSkills
  }, [FrontEndSkills])

  const backendSkills = useMemo(() => {
    return BackEndSkills
  }, [BackEndSkills])

  const otherSkills = useMemo(() => {
    return OtherSkills
  }, [OtherSkills])

  const updateTabValue = useCallback(() => {
    const nextIndex = (tabIndex + 1) % skillTabs.length
    setTabIndex(nextIndex)
    setActiveTab(skillTabs[nextIndex])
    handleCategoryChange(skillTabs[nextIndex])
  }, [tabIndex, skillTabs, handleCategoryChange])

  const handleTabChange = useCallback(
    (value: string) => {
      setActiveTab(value)
      setTabIndex(skillTabs.indexOf(value as SkillTabPanelList))
      handleCategoryChange(value)
    },
    [handleCategoryChange, skillTabs],
  )

  const { classes } = useStyles({ backgroundColor, isMobile })

  return (
    <Box>
      <Tabs
        defaultValue={SkillTabPanelList.Frontend}
        variant="pills"
        radius={'xl'}
        value={activeTab}
        onTabChange={handleTabChange}
        orientation="horizontal"
      >
        <Group position="center" align="center">
          <Fade bottom>
            <Box className={classes.tabListWrapper}>
              <Tabs.List>
                <Tabs.Tab value={SkillTabPanelList.Frontend} bg={backgroundColor}>
                  <Text weight={700} size={'md'}>
                    Frontend
                  </Text>
                </Tabs.Tab>
                <Tabs.Tab value={SkillTabPanelList.Backend} bg={backgroundColor}>
                  <Text weight={700} size={'md'}>
                    Backend
                  </Text>
                </Tabs.Tab>
                <Tabs.Tab value={SkillTabPanelList.Other} bg={backgroundColor}>
                  <Text weight={700} size={'md'}>
                    Other
                  </Text>
                </Tabs.Tab>
              </Tabs.List>
            </Box>
          </Fade>
        </Group>
        <Group className={classes.skillsCardWrapper} onClick={updateTabValue}>
          <Fade cascade>
            <Tabs.Panel value={SkillTabPanelList.Frontend} pt="xs">
              <Group position="center" align="flex-start">
                {frontendSkills.map(skill => (
                  <SkillCard skillName={skill.name} skillIcon={skill.image} key={skill.name} />
                ))}
              </Group>
            </Tabs.Panel>

            <Tabs.Panel value={SkillTabPanelList.Backend} pt="xs">
              <Group position="center" align="flex-start">
                {backendSkills.map(skill => (
                  <SkillCard skillName={skill.name} skillIcon={skill.image} key={skill.name} />
                ))}
              </Group>
            </Tabs.Panel>

            <Tabs.Panel value={SkillTabPanelList.Other} pt="xs">
              <Group position="center" align="flex-start">
                {otherSkills.map(skill => (
                  <SkillCard skillName={skill.name} skillIcon={skill.image} key={skill.name} />
                ))}
              </Group>
            </Tabs.Panel>
          </Fade>
        </Group>
      </Tabs>
    </Box>
  )
}
